import { useState, useEffect, useRef, useCallback } from "react";
import { User, MapPin, Briefcase, Sparkles, MessageCircle, X, FileText } from "lucide-react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { MembershipBadge } from "./MembershipBadge";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AVATAR_COLORS = {
  vip: "from-purple-400 to-purple-600",
  premium: "from-amber-400 to-amber-600",
  regular: "from-emerald-400 to-emerald-600",
  basic: "from-slate-300 to-slate-500",
};

const GENDER_LABELS = { male: "남성", female: "여성" };
const EMPTY = "미입력";

export const AuthorProfilePopover = ({ authorId, authorName, authorMembership, position, children }) => {
  const [open, setOpen] = useState(false);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const triggerRef = useRef(null);
  const popRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    const handleClick = (e) => {
      if (popRef.current && !popRef.current.contains(e.target) &&
          triggerRef.current && !triggerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const calcCoords = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const popW = 288; // w-72 = 18rem = 288px
    const popH = 380;

    if (position === "left") {
      // 왼쪽에 공간이 충분하면 왼쪽, 아니면 아래
      const leftSpace = rect.left;
      if (leftSpace >= popW + 8) {
        setCoords({ top: rect.top, left: rect.left - popW - 8 });
      } else {
        setCoords({ top: rect.bottom + 8, left: Math.max(8, rect.left - popW / 2) });
      }
    } else {
      // 기본: 아래에 표시
      const belowSpace = window.innerHeight - rect.bottom;
      if (belowSpace >= popH) {
        setCoords({ top: rect.bottom + 8, left: rect.left });
      } else {
        setCoords({ top: rect.top - popH - 8, left: rect.left });
      }
    }
  }, [position]);

  const handleOpen = async () => {
    if (!authorId) return;
    calcCoords();
    setOpen(true);
    if (profile) return;
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/users/${authorId}/public-profile`);
      if (res.ok) setProfile(await res.json());
    } catch { /* ignore */ }
    setLoading(false);
  };

  const gradient = AVATAR_COLORS[authorMembership] || AVATAR_COLORS.basic;

  const genderAge = () => {
    const g = GENDER_LABELS[profile?.gender];
    const a = profile?.age;
    if (g && a) return `${g}, ${a}세`;
    if (g) return g;
    if (a) return `${a}세`;
    return EMPTY;
  };

  const location = () => {
    const parts = [profile?.country, profile?.city].filter(Boolean);
    return parts.length ? parts.join(" ") : EMPTY;
  };

  const jobInfo = () => {
    const parts = [profile?.job, profile?.company_name].filter(Boolean);
    return parts.length ? parts.join(" / ") : EMPTY;
  };

  const mbtiHeight = () => {
    const parts = [profile?.mbti, profile?.height ? `${profile.height}cm` : ""].filter(Boolean);
    return parts.length ? parts.join(" / ") : EMPTY;
  };

  const popover = open ? createPortal(
    <div
      ref={popRef}
      onClick={(e) => e.stopPropagation()}
      className="fixed z-[9999] w-72 bg-white rounded-xl shadow-2xl border border-stone-200 overflow-hidden"
      style={{ top: coords.top, left: coords.left, animation: "popFadeIn 0.2s ease" }}
      data-testid="author-profile-popover"
    >
      {loading ? (
        <div className="p-6 text-center text-stone-400 text-sm">불러오는 중...</div>
      ) : profile ? (
        <>
          <div className={`bg-gradient-to-r ${gradient} p-4 relative`}>
            <button onClick={() => setOpen(false)} className="absolute top-2 right-2 text-white/70 hover:text-white" data-testid="popover-close-btn">
              <X className="w-4 h-4" />
            </button>
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center border-2 border-white/40">
                <span className="text-white font-bold text-xl">{profile.username?.[0] || "?"}</span>
              </div>
              <div>
                <div className="text-white font-bold text-lg">{profile.username}</div>
                <MembershipBadge membership={profile.membership_type} size="sm" />
              </div>
            </div>
          </div>

          <div className="p-4 space-y-2.5">
            <div className="flex items-center gap-2 text-sm">
              <User className="w-4 h-4 text-stone-400 flex-shrink-0" />
              <span className={profile.gender || profile.age ? "text-stone-700" : "text-stone-300"}>
                {genderAge()}
              </span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <MapPin className="w-4 h-4 text-stone-400 flex-shrink-0" />
              <span className={profile.country || profile.city ? "text-stone-700" : "text-stone-300"}>
                {location()}
              </span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Briefcase className="w-4 h-4 text-stone-400 flex-shrink-0" />
              <span className={profile.job ? "text-stone-700" : "text-stone-300"}>
                {jobInfo()}
              </span>
            </div>
            <div className="flex items-start gap-2 text-sm">
              <Sparkles className="w-4 h-4 text-stone-400 flex-shrink-0 mt-0.5" />
              {profile.personality && profile.personality.length > 0 ? (
                <div className="flex flex-wrap gap-1">
                  {profile.personality.map((p, i) => (
                    <span key={i} className="px-2 py-0.5 bg-stone-100 text-stone-600 rounded-full text-xs">{p}</span>
                  ))}
                </div>
              ) : (
                <span className="text-stone-300">{EMPTY}</span>
              )}
            </div>
            <div className="pt-2 border-t border-stone-100">
              <div className="flex items-start gap-2 text-sm">
                <MessageCircle className="w-4 h-4 text-stone-400 flex-shrink-0 mt-0.5" />
                <p className={`leading-relaxed text-xs line-clamp-3 ${profile.introduction ? "text-stone-600" : "text-stone-300"}`}>
                  {profile.introduction || EMPTY}
                </p>
              </div>
            </div>

            {/* 모든 글 읽기 버튼 */}
            <button
              onClick={() => { setOpen(false); navigate(`/community/author/${authorId}`); }}
              className="w-full mt-2 flex items-center justify-center gap-1.5 py-2 bg-stone-50 hover:bg-stone-100 text-stone-600 rounded-lg text-sm font-medium transition-colors"
              data-testid="view-all-posts-btn"
            >
              <FileText className="w-4 h-4" />
              {profile.username}님의 모든 글 읽기
            </button>
          </div>
        </>
      ) : (
        <div className="p-6 text-center text-stone-400 text-sm">프로필을 불러올 수 없습니다</div>
      )}
    </div>,
    document.body
  ) : null;

  return (
    <span className="relative inline-block">
      <span ref={triggerRef} onClick={(e) => { e.stopPropagation(); e.preventDefault(); handleOpen(); }} className="cursor-pointer" data-testid="author-avatar-click">
        {children}
      </span>
      {popover}
      <style>{`
        @keyframes popFadeIn {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </span>
  );
};

export default AuthorProfilePopover;
